// NOAA Coastal Lidar — browse NOAA's public coastal lidar holdings on the map and copy
// chosen LAZ tiles straight into the active project's DAM (server-side S3 copy; nothing
// is downloaded through the browser). The catalog itself is built nightly by the server
// (services/ais-api/src/noaa-lidar.js) from NOAA's tile index GeoPackages.
import L from "leaflet";

const API = "/api/noaa-lidar";
const PREFS_KEY = "hydrotools:noaa-lidar:v1";
// Below this zoom a dataset's tiles are too small to click and too many to send.
const TILE_MIN_ZOOM = 11;

function esc(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function fmtBytes(n) {
  const v = Number(n) || 0;
  if (v >= 1e9) return `${(v / 1e9).toFixed(2)} GB`;
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)} MB`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(0)} KB`;
  return `${v} B`;
}

function fmtWhen(iso) {
  if (!iso) return "never";
  const d = new Date(iso);
  return Number.isFinite(d.getTime()) ? d.toLocaleString() : "never";
}

// Stable per-dataset hue so overlapping footprints stay distinguishable between redraws.
function datasetColor(id) {
  let h = 0;
  for (const ch of String(id)) h = (h * 31 + ch.charCodeAt(0)) % 360;
  return `hsl(${h}, 75%, 55%)`;
}

const normDatum = (s) => String(s || "").toUpperCase().replace(/[^A-Z0-9]/g, "");

// Warnings for importing this dataset into this project. Imports are never blocked —
// the DAM's LAS CRS check flags the files and its Fix action can reproject them — but
// the user should know before copying hundreds of tiles.
function compatibilityNotes(ds, project) {
  if (!ds || !project) return [];
  const notes = [];
  const pH = String(project.horizontalCRS || "").toUpperCase();
  const dH = String(ds.horizontalEpsg || "").toUpperCase();
  if (dH && pH && pH !== "EPSG:4326" && dH !== pH) {
    notes.push(`Tiles are in ${dH}${ds.horizontalDesc ? ` (${ds.horizontalDesc})` : ""}; the project is ${pH}. The DAM's LAS CRS check will flag them, and its Fix action can reproject.`);
  } else if (!dH && ds.reportedEpsg && ds.horizontalDesc && pH) {
    notes.push(`Tiles are in ${ds.horizontalDesc}, which has no EPSG code (NOAA's index lists ${ds.reportedEpsg}, the lat/lon base); the project is ${pH}. Check the tiles' own CRS in the DAM before use.`);
  }
  const pV = normDatum(project.verticalDatum);
  if (pV && pV !== "NONE" && ds.verticalDesc && !normDatum(ds.verticalDesc).includes(pV)) {
    notes.push(`Vertical datum is ${ds.verticalDesc}; the project uses ${project.verticalDatum}. Elevations are not converted on import.`);
  }
  return notes;
}

function createRuntime(app) {
  const map = app?.map?.instance;
  let containerEl = null;
  let mounted = false;
  let panelOpen = false;
  // Set while the tool itself is being disabled, so layer-removal callbacks don't read the
  // teardown as the user switching coverage off.
  let tearingDown = false;

  let catalog = null;
  let catalogError = "";
  let datasets = [];
  let tileCounts = false;
  let listBusy = false;
  let listError = "";
  let listSeq = 0;

  const prefs = (() => {
    try {
      const p = JSON.parse(localStorage.getItem(PREFS_KEY) || "null") || {};
      return { q: String(p.q || ""), datum: String(p.datum || ""), yearMin: String(p.yearMin || ""), coverage: p.coverage !== false };
    } catch {
      return { q: "", datum: "", yearMin: "", coverage: true };
    }
  })();
  function savePrefs() {
    try { localStorage.setItem(PREFS_KEY, JSON.stringify(prefs)); } catch {}
  }

  let activeDs = null;
  let tilesInfo = "";
  let tilesSeq = 0;
  const tileLayers = new Map(); // key -> leaflet layer
  const selected = new Map(); // key -> { filename, rel }
  let boxMode = false;
  let boxStart = null;
  let boxRect = null;
  let lastTileClickAt = 0;

  let folder = "";
  let importBusy = false;
  let jobs = [];
  let pollTimer = null;
  let moveTimer = null;

  // ── Map layers ────────────────────────────────────────────────────────────────────────
  const coverageLayer = L.geoJSON(null, {
    interactive: false,
    // Footprints overlap heavily along most coastlines, so fills stay faint; with a dataset
    // open, the others drop to faint outlines so its tile grid reads clearly.
    style: (f) => {
      const c = datasetColor(f?.properties?.id);
      if (!activeDs) return { color: c, weight: 1.2, opacity: 0.85, fillColor: c, fillOpacity: 0.06 };
      const active = f?.properties?.id === activeDs.id;
      return { color: c, weight: active ? 2.5 : 1, opacity: active ? 0.95 : 0.3, fillColor: c, fillOpacity: 0 };
    },
  });
  const tilesLayer = L.geoJSON(null, {
    style: (f) => tileStyle(selected.has(f?.properties?.key)),
    onEachFeature: (feature, layer) => {
      const key = feature?.properties?.key;
      tileLayers.set(key, layer);
      layer.bindTooltip(esc(feature?.properties?.filename || ""), { sticky: true, direction: "top", opacity: 0.9 });
      layer.on("click", () => {
        lastTileClickAt = Date.now();
        if (boxMode) return;
        toggleTile(feature);
      });
    },
  });
  let coverageLayerId = null;
  let tilesLayerId = null;

  function tileStyle(isSelected) {
    return isSelected
      ? { color: "#f59e0b", weight: 1.5, opacity: 1, fillColor: "#f59e0b", fillOpacity: 0.35 }
      : { color: "#38bdf8", weight: 1, opacity: 0.85, fillColor: "#38bdf8", fillOpacity: 0.06 };
  }

  function managedLayer(name, leafletLayer, onRemove) {
    return {
      name,
      kind: "overlay",
      group: "NOAA Coastal Lidar",
      type: "managed",
      visible: true,
      opacity: 1,
      volatile: true,
      managed: {
        setVisible(v) {
          if (v && !map.hasLayer(leafletLayer)) leafletLayer.addTo(map);
          else if (!v && map.hasLayer(leafletLayer)) map.removeLayer(leafletLayer);
        },
        setOpacity() {},
        getBounds() {
          const b = leafletLayer.getBounds?.();
          return b?.isValid?.() ? b : null;
        },
        bringToFront() { leafletLayer.bringToFront?.(); },
        remove() {
          if (map.hasLayer(leafletLayer)) map.removeLayer(leafletLayer);
          onRemove();
        },
      },
    };
  }

  function showCoverageLayer() {
    if (coverageLayerId) return;
    coverageLayerId = app.services.layers.addLayer("noaa-lidar", managedLayer("NOAA Lidar Coverage", coverageLayer, () => {
      coverageLayerId = null;
      if (prefs.coverage && !tearingDown) {
        prefs.coverage = false;
        savePrefs();
        const box = $("noaa-coverage");
        if (box) box.checked = false;
      }
    }));
  }

  function hideCoverageLayer() {
    if (!coverageLayerId) return;
    const id = coverageLayerId;
    coverageLayerId = null;
    app.services.layers.removeLayer(id);
  }

  function showTilesLayer() {
    if (tilesLayerId) return;
    tilesLayerId = app.services.layers.addLayer("noaa-lidar", managedLayer(`NOAA Lidar Tiles: ${activeDs?.title || ""}`, tilesLayer, () => {
      tilesLayerId = null;
      if (activeDs && !tearingDown) closeDataset();
    }));
  }

  // ── Data ──────────────────────────────────────────────────────────────────────────────
  async function getJson(url, opts) {
    const res = await fetch(url, opts);
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data?.ok === false) throw new Error(data?.error || `HTTP ${res.status}`);
    await fixDatasetEpsgs(data);
    return data;
  }

  // NOAA's tile indexes give some datasets the BASE geographic code (e.g. EPSG:6318, plain
  // NAD83(2011) lat/lon) while the dataset's own metadata names the projected system the tiles are
  // in ("NAD83(2011) / UTM zone 55N") -- 10 codes / ~135 datasets in the 2026-10 catalog. When the
  // code is geographic and the description names a projected CRS, use the EPSG catalogue's code for
  // that exact name (projections.js); otherwise leave it alone.
  async function fixDatasetEpsgs(data) {
    const svc = app.services.projections;
    if (!svc?.searchCRS) return;
    const seen = new Set();
    const walk = (o, depth) => {
      if (!o || typeof o !== "object" || depth > 3 || seen.has(o)) return [];
      seen.add(o);
      const hits = "horizontalEpsg" in o && o.horizontalDesc ? [o] : [];
      for (const v of Array.isArray(o) ? o : Object.values(o)) hits.push(...walk(v, depth + 1));
      return hits;
    };
    const dss = walk(data, 0);
    if (!dss.length) return;
    await svc.loadCatalog().catch(() => {});
    for (const ds of dss) {
      const cur = svc.getCRS(ds.horizontalEpsg);
      const desc = String(ds.horizontalDesc || "").trim();
      if (!desc.includes("/") || (cur && cur.kind !== "geographic")) continue;
      const norm = (t) => t.toLowerCase().replace(/\s+/g, " ");
      const exact = svc.searchCRS(desc, { kind: "projected", limit: 20 }).find((e) => norm(e.name) === norm(desc));
      ds.reportedEpsg = ds.horizontalEpsg;
      // no EPSG code exists for some of these (e.g. NAD83(2011) / UTM zone 55N, CORS96 UTM zones):
      // NOAA used a custom definition -- say so instead of reporting a lat/lon code
      ds.horizontalEpsg = exact ? exact.code : "";
    }
  }

  function viewBbox(pad = 0) {
    const b = map.getBounds().pad(pad);
    return [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()].map((n) => n.toFixed(6)).join(",");
  }

  async function loadCatalogStatus() {
    try {
      catalog = await getJson(`${API}/status`);
      catalogError = "";
    } catch (err) {
      catalogError = String(err?.message || err);
    }
    renderCatalog();
    if (catalog?.sync && mounted) setTimeout(() => mounted && loadCatalogStatus(), 5000);
  }

  async function loadDatasets() {
    const seq = ++listSeq;
    listBusy = true;
    renderList();
    const qs = new URLSearchParams({ bbox: viewBbox() });
    if (prefs.q.trim()) qs.set("q", prefs.q.trim());
    if (prefs.datum) qs.set("datum", prefs.datum);
    if (prefs.yearMin) qs.set("yearMin", prefs.yearMin);
    try {
      const data = await getJson(`${API}/datasets?${qs}`);
      if (seq !== listSeq) return;
      datasets = data.datasets || [];
      tileCounts = !!data.tileCounts;
      listError = "";
    } catch (err) {
      if (seq !== listSeq) return;
      listError = String(err?.message || err);
    } finally {
      if (seq === listSeq) listBusy = false;
    }
    renderList();
  }

  let coverageSeq = 0;
  async function loadCoverage() {
    if (!prefs.coverage) return;
    const seq = ++coverageSeq;
    const ids = datasets.map((d) => d.id);
    const qs = new URLSearchParams({ bbox: viewBbox(0.2), zoom: String(map.getZoom()) });
    // Keep the drawn footprints in step with the list's filters.
    if (prefs.q.trim() || prefs.datum || prefs.yearMin) qs.set("ids", ids.join(","));
    try {
      const res = await fetch(`${API}/footprints?${qs}`);
      const fc = await res.json();
      if (seq !== coverageSeq || !prefs.coverage) return;
      coverageLayer.clearLayers();
      if (ids.length || !(prefs.q.trim() || prefs.datum || prefs.yearMin)) coverageLayer.addData(fc);
    } catch {}
  }

  async function loadTiles() {
    if (!activeDs) return;
    const seq = ++tilesSeq;
    if (map.getZoom() < TILE_MIN_ZOOM) {
      tilesLayer.clearLayers();
      tileLayers.clear();
      tilesInfo = `Zoom in (level ${TILE_MIN_ZOOM}+) to see and pick tiles.`;
      renderTiles();
      return;
    }
    tilesInfo = "Loading tiles…";
    renderTiles();
    try {
      const res = await fetch(`${API}/datasets/${activeDs.id}/tiles?bbox=${viewBbox(0.1)}`);
      const fc = await res.json();
      if (seq !== tilesSeq || !activeDs) return;
      if (!res.ok) throw new Error(fc?.error || `HTTP ${res.status}`);
      tilesLayer.clearLayers();
      tileLayers.clear();
      tilesLayer.addData(fc);
      tilesInfo = fc.truncated
        ? `Showing the first ${fc.limit} tiles here — zoom in to see the rest.`
        : `${fc.features.length} tile(s) in view.`;
    } catch (err) {
      if (seq !== tilesSeq) return;
      tilesInfo = `Failed to load tiles: ${String(err?.message || err)}`;
    }
    renderTiles();
  }

  function onMoveEnd() {
    clearTimeout(moveTimer);
    moveTimer = setTimeout(async () => {
      if (!mounted) return;
      await loadDatasets();
      loadCoverage();
      if (activeDs) loadTiles();
    }, 350);
  }

  // Clicking the map with coverage showing lists every dataset with tiles at that spot —
  // footprints overlap heavily, so a popup on whichever polygon is on top would hide the rest.
  async function onMapClick(e) {
    if (!mounted || !panelOpen || !prefs.coverage || boxMode || Date.now() - lastTileClickAt < 300) return;
    const d = 0.0005;
    const { lat, lng } = e.latlng;
    try {
      const data = await getJson(`${API}/datasets?bbox=${[lng - d, lat - d, lng + d, lat + d].map((n) => n.toFixed(6)).join(",")}`);
      const list = data.datasets || [];
      if (!list.length) return;
      const html = `
        <div style="max-height:260px; overflow:auto; min-width:240px;">
          <div style="font-weight:600; margin-bottom:4px;">NOAA lidar here (${list.length})</div>
          ${list.map((ds) => `
            <div style="padding:4px 0; border-top:1px solid rgba(127,127,127,0.25);">
              <div>${esc(ds.title)}</div>
              <div style="opacity:0.75; font-size:11px;">${esc(ds.datum)} · ${esc(ds.horizontalEpsg || (ds.reportedEpsg ? `${ds.horizontalDesc} (no EPSG)` : ""))} · ${ds.tileCount ?? "?"} tiles</div>
              <button class="btn small" data-noaa-open="${esc(ds.id)}" style="margin-top:3px;">Show tiles</button>
            </div>`).join("")}
        </div>`;
      popupDatasets = list;
      L.popup({ maxWidth: 360 }).setLatLng(e.latlng).setContent(html).openOn(map);
    } catch {}
  }
  let popupDatasets = [];
  function onMapDomClick(e) {
    const btn = e.target?.closest?.("[data-noaa-open]");
    if (!btn) return;
    const ds = popupDatasets.find((x) => x.id === btn.getAttribute("data-noaa-open")) || datasets.find((x) => x.id === btn.getAttribute("data-noaa-open"));
    if (!ds) return;
    map.closePopup();
    openDataset(ds);
  }

  // ── Selection ─────────────────────────────────────────────────────────────────────────
  function toggleTile(feature) {
    const key = feature?.properties?.key;
    if (!key) return;
    if (selected.has(key)) selected.delete(key);
    else selected.set(key, { filename: feature.properties.filename, rel: feature.properties.rel });
    tileLayers.get(key)?.setStyle(tileStyle(selected.has(key)));
    renderImport();
  }

  function selectWhere(test) {
    tilesLayer.eachLayer((layer) => {
      const p = layer.feature?.properties;
      if (!p?.key || !test(layer)) return;
      selected.set(p.key, { filename: p.filename, rel: p.rel });
      layer.setStyle(tileStyle(true));
    });
    renderImport();
  }

  function clearSelection() {
    selected.clear();
    tilesLayer.eachLayer((layer) => layer.setStyle(tileStyle(false)));
    renderImport();
  }

  function setBoxMode(on) {
    boxMode = on;
    map.getContainer().style.cursor = on ? "crosshair" : "";
    if (on) map.dragging.disable();
    else map.dragging.enable();
    renderTiles();
  }

  function onBoxDown(e) {
    if (!boxMode) return;
    boxStart = e.latlng;
    boxRect = L.rectangle(L.latLngBounds(boxStart, boxStart), { color: "#f59e0b", weight: 1, dashArray: "4 4", fillOpacity: 0.08, interactive: false }).addTo(map);
  }
  function onBoxMove(e) {
    if (boxMode && boxStart && boxRect) boxRect.setBounds(L.latLngBounds(boxStart, e.latlng));
  }
  function onBoxUp(e) {
    if (!boxMode || !boxStart) return;
    const bounds = L.latLngBounds(boxStart, e.latlng);
    boxStart = null;
    if (boxRect) { map.removeLayer(boxRect); boxRect = null; }
    setBoxMode(false);
    selectWhere((layer) => layer.getBounds?.()?.intersects(bounds));
  }

  // ── Dataset focus ─────────────────────────────────────────────────────────────────────
  function openDataset(ds) {
    if (activeDs?.id !== ds.id) {
      clearSelection();
      tilesLayer.clearLayers();
      tileLayers.clear();
      if (tilesLayerId) {
        const id = tilesLayerId;
        tilesLayerId = null;
        app.services.layers.removeLayer(id);
      }
    }
    activeDs = ds;
    folder = `noaa-${ds.missionId}`;
    showTilesLayer();
    coverageLayer.setStyle(coverageLayer.options.style);
    // Datasets picked at a regional zoom: fly in far enough for tiles to be pickable.
    if (map.getZoom() < TILE_MIN_ZOOM && ds.bbox) {
      const [w, s, e, n] = ds.bbox;
      const b = L.latLngBounds([s, w], [n, e]);
      if (!b.contains(map.getCenter())) map.fitBounds(b);
      else map.setZoom(TILE_MIN_ZOOM);
    } else {
      loadTiles();
    }
    refresh();
  }

  function closeDataset() {
    activeDs = null;
    selected.clear();
    tilesLayer.clearLayers();
    tileLayers.clear();
    if (tilesLayerId) {
      const id = tilesLayerId;
      tilesLayerId = null;
      app.services.layers.removeLayer(id);
    }
    if (boxMode) setBoxMode(false);
    coverageLayer.setStyle(coverageLayer.options.style);
    refresh();
  }

  // ── Import ────────────────────────────────────────────────────────────────────────────
  const activeProject = () => app?.services?.context?.getActiveProject?.() || null;

  async function startImport() {
    const project = activeProject();
    if (!project?.code) { app.ui?.toast?.("Activate a project first."); return; }
    if (!activeDs || !selected.size) return;
    importBusy = true;
    renderImport();
    try {
      const data = await getJson(`${API}/import`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: project.code, datasetId: activeDs.id, keys: [...selected.keys()], folder }),
      });
      jobs = [data.job, ...jobs.filter((j) => j.id !== data.job.id)];
      app.ui?.toast?.(`Importing ${data.job.total} tile(s) into ${project.code}…`);
      clearSelection();
      startPolling();
    } catch (err) {
      app.ui?.toast?.(`Import failed to start: ${String(err?.message || err)}`);
    } finally {
      importBusy = false;
      renderImport();
    }
  }

  async function pollJobs() {
    const project = activeProject();
    try {
      const data = await getJson(`${API}/import/jobs${project?.code ? `?code=${encodeURIComponent(project.code)}` : ""}`);
      const before = new Map(jobs.map((j) => [j.id, j.status]));
      jobs = data.jobs || [];
      for (const j of jobs) {
        if (before.get(j.id) === "running" && j.status !== "running") {
          app.ui?.toast?.(j.status === "done"
            ? `NOAA import done — ${j.copied} copied, ${j.skipped} already in the DAM.`
            : `NOAA import ${j.status} — ${j.copied} copied, ${j.errorCount} failed.`);
        }
      }
    } catch {}
    if (!jobs.some((j) => j.status === "running")) stopPolling();
    renderImport();
  }
  function startPolling() {
    if (!pollTimer) pollTimer = setInterval(pollJobs, 2000);
  }
  function stopPolling() {
    clearInterval(pollTimer);
    pollTimer = null;
  }

  async function startSync() {
    try {
      await getJson(`${API}/sync`, { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
      app.ui?.toast?.("NOAA catalog sync started.");
    } catch (err) {
      app.ui?.toast?.(`Sync failed to start: ${String(err?.message || err)}`);
    }
    loadCatalogStatus();
  }

  // ── Rendering ─────────────────────────────────────────────────────────────────────────
  const $ = (id) => containerEl?.querySelector(`#${id}`) || null;
  const isAdmin = () => (app?.auth?.groups || []).includes("admin");

  function catalogHtml() {
    if (catalogError) return `<div class="small" style="color:#ef4444;">Catalog unavailable: ${esc(catalogError)}</div>`;
    if (!catalog) return `<div class="small" style="color:var(--muted);">Loading catalog…</div>`;
    const s = catalog.sync;
    const last = catalog.lastRun;
    return `
      <div class="small" style="color:var(--muted);">
        ${catalog.datasets.toLocaleString()} datasets · ${catalog.tiles.toLocaleString()} tiles · last synced ${esc(fmtWhen(catalog.lastGoodSyncAt))}
      </div>
      ${s ? `<div class="small" style="margin-top:4px;">Syncing… ${s.done}/${s.total || "?"} datasets checked, ${s.loaded} updated${s.failed ? `, ${s.failed} failed` : ""}.</div>` : ""}
      ${!s && catalog.stale ? `<div class="small" style="color:#f59e0b; margin-top:4px;">${catalog.lastGoodSyncAt ? "The catalog hasn't synced with NOAA in over 3 days" : "The catalog has never been synced"}${last?.status === "failed" ? ` — last attempt failed: ${esc(last.error || "unknown error")}` : ""}.</div>` : ""}
      ${isAdmin() ? `<div class="row" style="margin-top:6px;"><button id="noaa-sync" class="btn small"${s ? " disabled" : ""}>Sync with NOAA now</button></div>` : ""}
    `;
  }

  function datasetCard(ds) {
    const active = activeDs?.id === ds.id;
    const notes = compatibilityNotes(ds, activeProject());
    return `
      <div class="noaa-ds" data-ds="${esc(ds.id)}" style="padding:6px 8px; border:1px solid ${active ? "var(--accent, #38bdf8)" : "rgba(127,127,127,0.25)"}; border-left:4px solid ${datasetColor(ds.id)}; border-radius:6px;">
        <div style="font-weight:600; font-size:13px; line-height:1.3;">${esc(ds.title)}</div>
        <div class="small" style="color:var(--muted); margin-top:2px;">
          ${esc(ds.datum)} · ${esc(ds.horizontalEpsg || (ds.reportedEpsg ? `${ds.horizontalDesc} (no EPSG)` : "CRS ?"))} · ${(ds.tileCount ?? 0).toLocaleString()} tiles${tileCounts && ds.tilesInArea != null ? ` · <b style="color:inherit;">${ds.tilesInArea.toLocaleString()} in view</b>` : ""}
        </div>
        ${ds.verticalDesc ? `<div class="small" style="color:var(--muted);">${esc(ds.verticalDesc)}</div>` : ""}
        ${notes.length ? `<div class="small" style="color:#f59e0b; margin-top:3px;" title="${esc(notes.join("\n"))}">⚠ ${esc(notes.length === 1 ? notes[0].split(";")[0] : "CRS and vertical datum differ from the project")}</div>` : ""}
        <div class="row" style="gap:6px; margin-top:5px; flex-wrap:wrap;">
          ${active
            ? `<button class="btn small" data-close-ds>Hide tiles</button>`
            : `<button class="btn small" data-open-ds="${esc(ds.id)}">Show tiles</button>`}
          ${ds.metadataUrl ? `<a class="small" href="${esc(ds.metadataUrl)}" target="_blank" rel="noopener">Metadata</a>` : ""}
          <a class="small" href="${esc(ds.indexUrl)}" target="_blank" rel="noopener">NOAA page</a>
        </div>
      </div>`;
  }

  function renderCatalog() {
    const host = $("noaa-catalog");
    if (host) host.innerHTML = catalogHtml();
  }

  // Partial re-render only: rebuilding the whole panel would reset which sections the
  // user has expanded (the shell restores that state only on its own renders).
  function refresh() {
    renderCatalog();
    renderList();
    renderTiles();
    renderImport();
  }

  function renderList() {
    const host = $("noaa-list");
    if (!host) return;
    const header = listError
      ? `<div class="small" style="color:#ef4444;">${esc(listError)}</div>`
      : `<div class="small" style="color:var(--muted);">${listBusy ? "Searching this area…" : `${datasets.length} dataset(s) ${tileCounts ? "with tiles in view" : "overlapping this view"}${!tileCounts && datasets.length ? " — zoom in for exact tile counts" : ""}.`}</div>`;
    host.innerHTML = `
      ${header}
      <div style="display:grid; gap:6px; margin-top:6px; max-height:420px; overflow:auto;">
        ${datasets.map(datasetCard).join("")}
      </div>`;
  }

  function renderTiles() {
    const host = $("noaa-tiles");
    if (!host) return;
    if (!activeDs) {
      host.innerHTML = `<div class="small" style="color:var(--muted);">Pick a dataset (Show tiles) to choose tiles to import.</div>`;
      return;
    }
    host.innerHTML = `
      <div style="font-weight:600; font-size:13px;">${esc(activeDs.title)}</div>
      <div class="small" style="color:var(--muted); margin:2px 0 6px;">${esc(tilesInfo)} Click tiles to select them.</div>
      <div class="row" style="gap:6px; flex-wrap:wrap;">
        <button id="noaa-box" class="btn small${boxMode ? " active" : ""}">${boxMode ? "Drag a box on the map…" : "Box select"}</button>
        <button id="noaa-select-view" class="btn small">Select all in view</button>
        <button id="noaa-clear" class="btn small"${selected.size ? "" : " disabled"}>Clear</button>
      </div>`;
  }

  function renderImport() {
    const host = $("noaa-import");
    if (!host) return;
    const project = activeProject();
    const notes = compatibilityNotes(activeDs, project);
    const running = jobs.find((j) => j.status === "running");
    host.innerHTML = `
      ${!project ? `<div class="small" style="color:var(--muted);">Activate a project to import tiles into its DAM.</div>` : `
        <div class="small" style="color:var(--muted);">Project <b style="color:inherit;">${esc(project.name || project.code)}</b> (${esc(project.code)})${project.location ? ` · <a href="#" id="noaa-goto-project">zoom to project</a>` : ""}</div>
        ${activeDs ? `
          <label class="small" style="display:block; margin-top:6px;">DAM folder (las-files/…)
            <input id="noaa-folder" type="text" value="${esc(folder)}" style="width:100%;" />
          </label>
          ${notes.map((n) => `<div class="small" style="color:#f59e0b; margin-top:4px;">⚠ ${esc(n)}</div>`).join("")}
          <div class="row" style="margin-top:8px;">
            <button id="noaa-import-go" class="btn"${!selected.size || importBusy || running ? " disabled" : ""}>
              ${importBusy ? "Starting…" : `Import ${selected.size} tile${selected.size === 1 ? "" : "s"} to DAM`}
            </button>
          </div>
          <div class="small" style="color:var(--muted); margin-top:4px;">Copied server-side from NOAA's S3 bucket; a tile index GPKG for the Potree Bulk Generator is written to vector-files.</div>
        ` : ""}
      `}
      ${jobs.length ? `
        <div style="display:grid; gap:6px; margin-top:10px;">
          ${jobs.slice(0, 5).map((j) => `
            <div class="small" style="padding:6px; border:1px solid rgba(127,127,127,0.25); border-radius:6px;">
              <div><b>${esc(j.code)}</b> ← ${esc(j.title || j.datasetId)}</div>
              <div style="color:var(--muted);">las-files/${esc(j.folder)} · ${j.status !== "running" ? esc(j.status) : j.phase === "tile-index" ? "writing tile index…" : `${j.done}/${j.total} tiles`} · ${j.copied} copied, ${j.skipped} already there${j.errorCount ? `, <span style="color:#ef4444;">${j.errorCount} failed</span>` : ""} · ${fmtBytes(j.bytesCopied)}</div>
              ${j.status === "running" && j.phase !== "tile-index" ? `<progress max="${j.total}" value="${j.done}" style="width:100%;"></progress> <button class="btn small" data-cancel-job="${esc(j.id)}">Cancel</button>` : ""}
              ${j.errors?.length ? `<details><summary>Errors</summary>${j.errors.map((e) => `<div>${esc(e.file)}: ${esc(e.error)}</div>`).join("")}</details>` : ""}
              ${j.tileIndexKey ? `<div style="color:var(--muted);">Tile index: vector-files/${esc(j.tileIndexKey.split("/vector-files/")[1] || "")}</div>` : ""}
            </div>`).join("")}
        </div>` : ""}
    `;
  }

  function render() {
    if (!containerEl) return;
    containerEl.innerHTML = `
      <div class="small" style="color:var(--muted); padding-bottom:8px;">
        NOAA's public coastal lidar (topographic and topobathy point clouds) for the area on the map.
        Pick a dataset, select tiles, and import them into the active project's DAM.
      </div>
      <div class="subtoolset-list" style="--subtoolset-bleed-top:0px;">
        <details class="toolset-group" open>
          <summary>Catalog</summary>
          <div class="toolset-content" id="noaa-catalog">${catalogHtml()}</div>
        </details>
        <details class="toolset-group" open>
          <summary>Datasets in this area</summary>
          <div class="toolset-content">
            <div class="row" style="gap:6px; flex-wrap:wrap;">
              <input id="noaa-q" type="search" placeholder="Filter by name…" value="${esc(prefs.q)}" style="flex:1; min-width:120px;" />
              <select id="noaa-datum" title="Vertical datum folder">
                ${[["", "Any datum"], ["geoid18", "NAVD88 (GEOID18)"], ["geoid12b", "GEOID12B"], ["mllw", "MLLW"], ["msl", "MSL"], ["igld85", "IGLD85"], ["egm08", "EGM08"]]
                  .map(([v, label]) => `<option value="${v}"${prefs.datum === v ? " selected" : ""}>${label}</option>`).join("")}
              </select>
              <select id="noaa-year" title="Collected in or after">
                ${["", "2020", "2015", "2010", "2005"].map((y) => `<option value="${y}"${prefs.yearMin === y ? " selected" : ""}>${y ? `${y}+` : "Any year"}</option>`).join("")}
              </select>
            </div>
            <label class="small" style="display:flex; gap:6px; align-items:center; margin-top:6px;">
              <input id="noaa-coverage" type="checkbox"${prefs.coverage ? " checked" : ""} /> Show coverage on map (click the map to list datasets at a spot)
            </label>
            <div id="noaa-list" style="margin-top:6px;"></div>
          </div>
        </details>
        <details class="toolset-group" open>
          <summary>Tiles</summary>
          <div class="toolset-content" id="noaa-tiles"></div>
        </details>
        <details class="toolset-group" open>
          <summary>Import to project DAM</summary>
          <div class="toolset-content" id="noaa-import"></div>
        </details>
      </div>`;
    renderList();
    renderTiles();
    renderImport();
  }

  function onClick(e) {
    const t = e.target;
    if (t.closest?.("#noaa-sync")) { startSync(); return; }
    const openBtn = t.closest?.("[data-open-ds]");
    if (openBtn) {
      const ds = datasets.find((d) => d.id === openBtn.getAttribute("data-open-ds"));
      if (ds) openDataset(ds);
      return;
    }
    if (t.closest?.("[data-close-ds]")) { closeDataset(); return; }
    if (t.closest?.("#noaa-box")) { setBoxMode(!boxMode); return; }
    if (t.closest?.("#noaa-select-view")) {
      const b = map.getBounds();
      selectWhere((layer) => layer.getBounds?.()?.intersects(b));
      return;
    }
    if (t.closest?.("#noaa-clear")) { clearSelection(); return; }
    if (t.closest?.("#noaa-import-go")) { startImport(); return; }
    const cancel = t.closest?.("[data-cancel-job]");
    if (cancel) {
      getJson(`${API}/import/cancel`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ id: cancel.getAttribute("data-cancel-job") }) })
        .then(pollJobs).catch(() => {});
      return;
    }
    if (t.closest?.("#noaa-goto-project")) {
      e.preventDefault();
      const loc = activeProject()?.location;
      if (loc) map.setView([loc.lat, loc.lng], Math.max(loc.zoom || 15, 14));
    }
  }

  let qTimer = null;
  function onInput(e) {
    if (e.target?.id === "noaa-q") {
      prefs.q = e.target.value;
      savePrefs();
      clearTimeout(qTimer);
      qTimer = setTimeout(() => loadDatasets().then(loadCoverage), 300);
    }
    if (e.target?.id === "noaa-folder") folder = e.target.value;
  }

  function onChange(e) {
    const id = e.target?.id;
    if (id === "noaa-datum" || id === "noaa-year") {
      if (id === "noaa-datum") prefs.datum = e.target.value;
      else prefs.yearMin = e.target.value;
      savePrefs();
      loadDatasets().then(loadCoverage);
    }
    if (id === "noaa-coverage") {
      prefs.coverage = !!e.target.checked;
      savePrefs();
      if (prefs.coverage) { showCoverageLayer(); loadCoverage(); } else { coverageLayer.clearLayers(); hideCoverageLayer(); }
    }
  }

  function onContextChanged() {
    jobs = [];
    pollJobs();
    renderList();
    renderImport();
  }

  function mount(el) {
    if (containerEl) {
      containerEl.removeEventListener("click", onClick);
      containerEl.removeEventListener("input", onInput);
      containerEl.removeEventListener("change", onChange);
    }
    containerEl = el;
    containerEl.addEventListener("click", onClick);
    containerEl.addEventListener("input", onInput);
    containerEl.addEventListener("change", onChange);
    render();
    if (!mounted) {
      mounted = true;
      map.on("moveend", onMoveEnd);
      map.on("click", onMapClick);
      map.on("mousedown", onBoxDown);
      map.on("mousemove", onBoxMove);
      map.on("mouseup", onBoxUp);
      map.getContainer().addEventListener("click", onMapDomClick, true);
      app.events?.on?.("context:changed", onContextChanged);
      if (prefs.coverage) showCoverageLayer();
      loadCatalogStatus();
      loadDatasets().then(loadCoverage);
      pollJobs();
    }
  }

  // Only called when the tool is disabled: stop the map listeners and take the layers down
  // (the shell's removeLayersByTool would too, but its callbacks can't tell that apart from
  // the user removing a layer).
  function destroy() {
    tearingDown = true;
    hideCoverageLayer();
    if (tilesLayerId) {
      const id = tilesLayerId;
      tilesLayerId = null;
      app.services.layers.removeLayer(id);
    }
    tearingDown = false;
    if (containerEl) {
      containerEl.removeEventListener("click", onClick);
      containerEl.removeEventListener("input", onInput);
      containerEl.removeEventListener("change", onChange);
    }
    containerEl = null;
    if (!mounted) return;
    mounted = false;
    if (boxMode) setBoxMode(false);
    clearTimeout(moveTimer);
    stopPolling();
    map.off("moveend", onMoveEnd);
    map.off("click", onMapClick);
    map.off("mousedown", onBoxDown);
    map.off("mousemove", onBoxMove);
    map.off("mouseup", onBoxUp);
    map.getContainer().removeEventListener("click", onMapDomClick, true);
    app.events?.off?.("context:changed", onContextChanged);
  }

  function setPanelOpen(open) {
    panelOpen = !!open;
    if (!panelOpen && boxMode) setBoxMode(false);
  }

  return { mount, destroy, setPanelOpen };
}

export const noaaLidarTool = {
  id: "noaa-coastal-lidar",
  name: "NOAA Coastal Lidar",
  init(app) { this._rt = this._rt ?? createRuntime(app); },
  destroy() { this._rt?.destroy?.(); this._rt = null; },
  renderPanel(app, el) {
    if (!this._rt) this._rt = createRuntime(app);
    this._rt.mount(el);
  },
  setPanelOpen(app, open) { this._rt?.setPanelOpen?.(open); },
};
